import React, { useState, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import PropTypes from 'prop-types';
import Text from 'components/Core/Text';
import RichTextTableOfContents from 'components/RichText/ContentfulRichText/TableOfContents';

import useWindowDimensions from 'hooks/useWindowDimensions';
import snackbar from 'utils/snackbar';
import GetDemo from 'components/CallToActions/GetDemo';
import { STIcon } from 'components/Brand/Icons/STIcon';
import EmailCapture from '../EmailCapture';
import NextPodcast from '../NextPodcast';
import { Container } from './styles';

const SidebarV2 = ({
  toc,
  demo,
  email,
  email2,
  podcast,
  customCta,
  classNames,
  setIsSidebarSticky,
  tocClassNames,
}) => {
  const [submittedEmail, hasSubmittedEmail] = useState(Cookies.get('email') || null);
  const [isSticky, setSticky] = useState(true);

  const [maxHeightBoundary, setMaxHeightBoundary] = useState(0);

  const windowHeight = useWindowDimensions().innerHeight;

  const getDemoRef = useRef();
  const emailCaptureRef = useRef();

  const showDetailedGetDemo =
    (submittedEmail && toc?.length <= 7) || (!toc && submittedEmail) || (!toc && !email);

  const handleSuccessfulSubmission = () => {
    hasSubmittedEmail(true);
    snackbar('Thank you for subscribing!');
  };

  useEffect(() => {
    const calculateDemoHeight = () => {
      const demoContainer = document.getElementsByClassName('sidebar-detailed-demo')[0];
      if (demoContainer) {
        const rect = demoContainer.getBoundingClientRect();
        return rect.height + 30;
      }
      return demo ? (showDetailedGetDemo || customCta ? 400 : 50) : 0;
    };

    const calculateEmailHeight = () => {
      if (email) {
        return !!submittedEmail ? 0 : 220;
      }
      if (email2) {
        return !!submittedEmail ? 0 : 250;
      }
      return 0;
    };

    const demoHeight = calculateDemoHeight();
    const emailHeight = calculateEmailHeight();
    const contentHeight = demoHeight + emailHeight;
    setMaxHeightBoundary(contentHeight);
  }, [toc]);

  useEffect(() => {
    if (podcast) {
      const isSticky = windowHeight - 150 > 900;
      setSticky(isSticky);
      setIsSidebarSticky(isSticky);
    }
  }, [windowHeight]);

  return (
    <Container className={classNames} sticky={isSticky}>
      {toc && (
        <RichTextTableOfContents
          data={toc}
          maxHeightBoundary={maxHeightBoundary}
          classNames={tocClassNames}
        />
      )}
      {podcast && podcast.post && <NextPodcast {...podcast} />}
      {email && !submittedEmail && (
        <EmailCapture
          ref={emailCaptureRef}
          classNames={'darkmode'}
          label={
            <DetailedLabel
              heading={['Get tips to learn', 'and succeed delivered', 'directly to your inbox.']}
            />
          }
          onSuccess={handleSuccessfulSubmission}
        />
      )}
      {email2 && !submittedEmail && (
        <EmailCapture
          ref={emailCaptureRef}
          label={'Advice delivered to your inbox'}
          onSuccess={handleSuccessfulSubmission}
        />
      )}
      {demo ? (
        customCta ? (
          <GetDemo ref={getDemoRef} showDetailedGetDemo customCta={customCta} />
        ) : (
          <GetDemo ref={getDemoRef} showDetailedGetDemo={!!showDetailedGetDemo} />
        )
      ) : null}
    </Container>
  );
};

export default SidebarV2;

SidebarV2.propTypes = {
  toc: PropTypes.array,
  podcast: PropTypes.object,
  demo: PropTypes.bool,
  email: PropTypes.bool,
  email2: PropTypes.bool,
  classNames: PropTypes.string,
  tocClassNames: PropTypes.string,
};

SidebarV2.defaultProps = {
  toc: null,
  podcast: null,
  email: false,
  email2: false,
  demo: false,
  classNames: '',
  tocClassNames: '',
};

const DetailedLabel = ({ heading, subhead }) => (
  <>
    <STIcon className="email-logo" type="mail" alt="Mail Icon" />
    {heading &&
      heading.map((el) => (
        <Text type="h5" className="email-title">
          {el}
        </Text>
      ))}
    {subhead && (
      <Text type="p" className="email-subhead" size="14px">
        {subhead}
      </Text>
    )}
  </>
);
